import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const Select = SelectPrimitive.Root;
const SelectValue = SelectPrimitive.Value;

function SelectTrigger({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        'para:border-border para:placeholder:text-muted-foreground para:flex para:h-11 para:w-full para:items-center para:justify-between para:rounded-md para:border para:bg-muted para:px-3 para:py-1 para:text-sm para:outline-none para:transition-[color,box-shadow] para:focus-visible:border-ring para:focus-visible:ring-ring/50 para:focus-visible:ring-[3px] para:disabled:pointer-events-none para:disabled:opacity-50 para:[&>span]:line-clamp-1',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="para:size-4 para:shrink-0 para:opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({ className, children, position = 'popper', ...props }: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        className={cn(
          'para:bg-card para:text-card-foreground para:relative para:z-50 para:max-h-96 para:min-w-[8rem] para:overflow-hidden para:rounded-lg para:border para:border-border para:shadow-md',
          'para:data-[state=open]:animate-in para:data-[state=closed]:animate-out para:data-[state=closed]:fade-out-0 para:data-[state=open]:fade-in-0 para:data-[state=closed]:zoom-out-95 para:data-[state=open]:zoom-in-95',
          position === 'popper' &&
            'para:data-[side=bottom]:translate-y-1 para:data-[side=top]:-translate-y-1',
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'para:p-1',
            position === 'popper' && 'para:w-full para:min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        'para:relative para:flex para:w-full para:cursor-pointer para:select-none para:items-center para:rounded-md para:py-2 para:pl-8 para:pr-3 para:text-sm para:outline-none para:transition-colors para:focus:bg-muted para:data-[disabled]:pointer-events-none para:data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="para:absolute para:left-2 para:flex para:size-3.5 para:items-center para:justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="para:size-3.5" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export { Select, SelectValue, SelectTrigger, SelectContent, SelectItem };
